import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';

import KiosImage from 'modules/quick-search/components/KiosImage';
import type { QsChannel } from 'modules/quick-search/types';

interface Props {
  channels: QsChannel[];
  selectedId?: number;
  onSelect: (channel: QsChannel) => void;
  loading?: boolean;
}

/** Hàng chip Kênh phân phối (KPP): ảnh + tên + "(N) PLHH". */
const ChannelChips: FC<Props> = ({ channels, selectedId, onSelect, loading }) => {
  const { formatMessage } = useIntl();

  if (loading && channels.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }} aria-busy="true">
        {Array.from({ length: 6 }, (_, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 210,
              height: 58,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Skeleton variant="rectangular" width={56} height={56} />
            <Box sx={{ px: 1.5, flexGrow: 1 }}>
              <Skeleton width="70%" height={20} />
              <Skeleton width="40%" height={14} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {channels.map((ch) => {
        const selected = ch.id === selectedId;
        return (
          <ButtonBase
            key={ch.id}
            onClick={() => onSelect(ch)}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: (theme) =>
                selected ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              transition: 'border-color .2s, background-color .2s',
              '&:hover': { borderColor: 'primary.main' },
              minWidth: 210,
              textAlign: 'left',
            }}
          >
            <KiosImage
              src={ch.image}
              alt={ch.name}
              fallbackIconSize={24}
              sx={{ width: 56, height: 56, objectFit: 'cover', bgcolor: 'action.hover', flexShrink: 0 }}
            />
            <Box sx={{ px: 1.5, py: 0.75, flexGrow: 1 }}>
              <Typography
                sx={{ fontWeight: 700, color: selected ? 'primary.main' : 'text.primary', lineHeight: 1.45 }}
              >
                {ch.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {formatMessage(
                  { id: 'quickSearch.classificationCount' },
                  { count: ch.classificationCount }
                )}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default ChannelChips;
